import type { RefObject } from 'react';
import { HiOutlineMegaphone } from 'react-icons/hi2';
import type { Message } from '../../contexts/ChatContext';
import { ChatComposer, ChatThread, MessageBubble, SystemMessage, type ChatComposerHandle } from '@reloam/ui';
import type { ChannelKind } from './conversationOrder';

/** Org-wide broadcast feed entry, as returned by the message store. */
interface BroadcastMessage {
  id?: number;
  content: string;
  created_at: string;
  sender_id?: number;
  is_broadcast?: boolean;
  sender?: { username?: string; contact?: { first_name?: string; last_name?: string } | null } | null;
}

interface PeerThreadProps {
  kind: Exclude<ChannelKind, 'assistant'>;
  peerName: string;
  messages: Message[];
  broadcastMessages: BroadcastMessage[];
  currentUserId?: number;
  /** Looks up a contact's display name from a sender id. */
  senderName: (senderId: string) => string | undefined;
  isSending: boolean;
  /** When set the composer is locked and shows this reason as its placeholder. */
  lockedReason?: string;
  onSend: (text: string) => void;
  composerRef: RefObject<ChatComposerHandle>;
  onDraftChange: (draft: string) => void;
}

const time = (value: number | string) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const PLACEHOLDER: Record<PeerThreadProps['kind'], (name: string) => string> = {
  tenant: (name) => (name ? `Message ${name}` : 'Type your message…'),
  sms: (name) => (name ? `SMS to ${name}` : 'Type an SMS…'),
  whatsapp: (name) => (name ? `WhatsApp to ${name}` : 'Type a WhatsApp message…'),
  service_desk: () => 'Reply to the service desk…',
};

function Bubble({ message, mine, label }: { message: Message; mine: boolean; label?: string }) {
  if (message.role === 'system') return <SystemMessage>{message.content}</SystemMessage>;
  return <MessageBubble mine={mine} content={message.content} senderLabel={label} timeLabel={time(message.timestamp)} />;
}

/**
 * A non-assistant conversation (tenant, SMS, WhatsApp, service desk): the same
 * message list, composer and dark-mode styling as the assistant thread, minus
 * the AI-only extras. Sending is wired by the parent exactly as before.
 */
export default function PeerThread(props: PeerThreadProps) {
  const { kind, peerName, messages, broadcastMessages, currentUserId, senderName, isSending, lockedReason, onSend, composerRef } = props;
  const showBroadcasts = broadcastMessages.length > 0;

  return (
    <ChatThread
      messageCount={messages.length + broadcastMessages.length}
      sending={isSending}
      emptyDescription={peerName ? `Say hello to ${peerName}.` : 'Start the conversation.'}
      composer={
        <ChatComposer
          ref={composerRef}
          ariaLabel={peerName ? `Message ${peerName}` : 'Type your message'}
          disabled={isSending}
          locked={Boolean(lockedReason)}
          placeholder={lockedReason || PLACEHOLDER[kind](peerName)}
          onSend={onSend}
          onDraftChange={props.onDraftChange}
        />
      }
    >
      {showBroadcasts && (
        <div className="flex flex-col gap-3">
          <p className="flex items-center gap-1.5 px-1 text-xs text-bodydark1 dark:text-bodydark2">
            <HiOutlineMegaphone className="h-3.5 w-3.5" aria-hidden /> Messages from tenants
          </p>
          {[...broadcastMessages]
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .map((m, i) => {
              const mine = m.sender_id === currentUserId;
              const name = m.sender?.contact
                ? `${m.sender.contact.first_name || ''} ${m.sender.contact.last_name || ''}`.trim() || m.sender.username
                : m.sender?.username;
              return (
                <Bubble
                  key={`broadcast-${m.id ?? i}`}
                  message={{ id: String(m.id ?? i), role: mine ? 'user' : 'tenant', content: m.content, timestamp: new Date(m.created_at).getTime(), read: true }}
                  mine={mine}
                  label={m.is_broadcast ? `${name || 'Broadcast'} · to organization` : name}
                />
              );
            })}
        </div>
      )}
      {messages.map((m) => {
        const mine = m.role === 'user' || Boolean(m.senderId && currentUserId && m.senderId === String(currentUserId));
        const label = m.role === 'agent' ? m.agentName || 'Agent' : m.senderId ? senderName(m.senderId) : undefined;
        return <Bubble key={m.id} message={m} mine={mine} label={label} />;
      })}
    </ChatThread>
  );
}
